import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cannesEventPriceFaq, cannesEventSeo } from "../slugs/eventFaq";

const cannesYachtingFestival: ChauffeurPage = {
  slug: "cannes-yachting-festival",
  title: "Private chauffeur Cannes Yachting Festival – UHNW VTC",
  subtitle:
    "Private chauffeur during the Cannes Yachting Festival: airport ↔ Vieux Port and Port Canto transfers, broker day hire and discreet UHNW service.",
  keywords: [
    "cannes yachting festival chauffeur",
    "cannes yachting festival private chauffeur",
    "yacht show chauffeur cannes",
  ],
  hero: {
    title: "Private chauffeur during the Cannes Yachting Festival",
    subtitle:
      "In mid-September, Europe's largest in-water boat show spreads across the Vieux Port and Port Canto: a discreet chauffeur for owners, brokers and visitors.",
    keywords: [
      "Cannes Yachting Festival chauffeur",
      "Vieux Port Cannes VTC",
      "Port Canto chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Cannes Yachting Festival",
    badges: ["Airport ↔ Vieux Port", "Port Canto shuttle", "UHNW discretion"],
  },
  benefits: {
    heading: "Why a chauffeur during the Yachting Festival",
    items: [
      {
        title: "Two ports to connect",
        description:
          "The show stretches across the Vieux Port and Port Canto, separated by the Croisette: the chauffeur shuttles between the two exhibition areas.",
      },
      {
        title: "Airport ↔ ports at peak",
        description:
          "Nice Côte d'Azur concentrates arrivals in mid-September: flight tracking, welcome and a direct link to the Jetée Albert Édouard or Port Canto.",
      },
      {
        title: "Broker / client day",
        description:
          "Yacht viewings, sea trials and meetings chain together: a dedicated chauffeur by the half-day or day keeps the pace.",
      },
      {
        title: "Discreet UHNW clientele",
        description:
          "Owners, shipyards and family offices: uniformed chauffeur, complete confidentiality and punctuality throughout the show.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Cannes Yachting Festival",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Cannes link with flight tracking, drop-off as close as possible to the Vieux Port or Port Canto depending on your meetings.",
      },
      {
        title: "Vieux Port ↔ Port Canto shuttle",
        text: "Back-and-forth between the two exhibition sites throughout the day, with no need to find parking on the Croisette.",
      },
      {
        title: "Broker hourly hire",
        text: "A dedicated chauffeur by the hour or day to chain boat viewings, sea trials and client meetings.",
      },
      {
        title: "Owner & crew runs",
        text: "Discreet trips between yacht, hotel and shipyards for owners, guests and crews during the show.",
      },
      {
        title: "Dinners & evenings",
        text: "Onboard receptions, Croisette dinners and business evenings, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Yachting Festival",
    items: [
      {
        name: "Vieux Port of Cannes",
        description:
          "Jetée Albert Édouard, Espace Riviera and large yachts afloat, the historic heart of the show.",
      },
      {
        name: "Port Canto",
        description:
          "The Sailing / yachts area at the other end of the Croisette.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic: lodging for exhibitors, brokers and visitors.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, arrivals concentrated in mid-September.",
      },
      {
        name: "Cap d'Antibes & hinterland",
        description: "Villas, private dinners and moorings of invited vessels.",
      },
    ],
  },
  generic: {
    heading: "The Cannes Yachting Festival from a chauffeur's seat",
    subheading:
      "In mid-September, Cannes becomes the world showcase for in-water yachting — an audience of owners, brokers and UHNW across two ports.",
    paragraphs: [
      "Every September, the Cannes Yachting Festival turns the bay into Europe's largest in-water boat show. The exhibition unfolds across two ports — the Vieux Port with its large-yacht area, and Port Canto dedicated to sailing — linked by the Croisette. A chauffeur who knows Cannes shuttles between the two sites without forcing you to hunt for parking.",
      "Arrivals concentrate at Nice Côte d'Azur airport over just a few days. Flight tracking, welcome and a direct link to the Jetée Albert Édouard or Port Canto absorb the week's peak and drop you as close as possible to your meetings.",
      "The show chains yacht viewings, sea trials, shipyard meetings and onboard dinners. Hourly hire keeps the same chauffeur for owners and brokers alike, with the discretion a UHNW clientele expects and late-night returns after receptions.",
    ],
    bullets: [
      "Vieux Port ↔ Port Canto shuttle",
      "Flight tracking in mid-September",
      "UHNW discretion",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Cannes Yachting Festival",
    steps: [
      "Tell us your dates, your hotel or yacht and your meetings (Vieux Port, Port Canto, shipyards).",
      "We plan the airport ↔ ports links and the shuttle between the two exhibition sites.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Daily and evening hire for viewings, sea trials and dinners.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("en", "during the Cannes Yachting Festival"),
    {
      q: "How do I book a private chauffeur during the Cannes Yachting Festival?",
      a: "Book ahead: in mid-September, Cannes is in high demand for the show. Share your dates, your hotel or yacht and your meetings so we can secure a dedicated chauffeur, with the discretion a UHNW clientele expects.",
    },
    {
      q: "Do you shuttle between the Vieux Port and Port Canto?",
      a: "Yes. The show is held across two ports separated by the Croisette; the chauffeur runs back and forth between the Jetée Albert Édouard and Port Canto all day, so you never have to look for parking between meetings.",
    },
    {
      q: "Do you handle the link between Nice airport and the Cannes ports?",
      a: "Yes, with flight tracking and welcome. We provide the direct Nice ↔ Cannes link and drop you as close as possible to the Vieux Port or Port Canto depending on your boat viewings and meetings.",
    },
    {
      q: "Can you also transport crews and suppliers?",
      a: "Yes. Beyond owners and guests, we organise runs for captains, crews and suppliers between yacht, hotel and shipyards throughout the show.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Cannes to Nice Airport",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Chauffeur Cannes to Saint-Tropez",
      href: "/en/chauffeur/transfers/cannes-saint-tropez",
    },
    {
      label: "Get a quote",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Cannes Yachting Festival",
    text: "Airport ↔ ports, Vieux Port / Port Canto shuttle and discreet service: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo(
      "en",
      "Cannes Yachting Festival",
      "Old Port and Port Canto.",
    ),
    canonical: "/en/chauffeur/cannes-yachting-festival",
  },
};

export default cannesYachtingFestival;
